import { useContext, useEffect, useState } from 'react';
import { TagsState, WP_Place } from '../model/map/map';
import { WP_Filter_Children, WP_Parent_Tag, WP_Tag } from '../model/tags/tags';
import { useLogger } from '../services/LogService';
import { AppContext } from '../state/AppContextProvider';
import { ActionType } from '../state/GlobalState';
import { useWP } from './WPHooks';

export const useFilterParents = (
  categories: WP_Parent_Tag[],
  onChange,
  multipleSelect: boolean
) => {
  const logger = useLogger('useTagFields : ');

  const initialTagsList = categories.map((category) => {
    return { parentTagId: category.id, tags: [] };
  });

  const [selectedSubTags, setSelectedSubTags] = useState<TagsState[]>([]);

  useEffect(() => {
    categories.length > 0 &&
      selectedSubTags.length === 0 &&
      setSelectedSubTags(initialTagsList);
    // eslint-disable-next-line react-hooks/rules-of-hooks, react-hooks/exhaustive-deps
  }, [categories]);

  const [selectedParentTagId, setSelectedParentTag] = useState<number | null>(
    null
  );

  const { childrens, fetchTags } = useFilterChildrens();

  const selectParentTag = (parentId, taxonomy) => {
    setSelectedParentTag(parentId);
    logger.info(
      `selectParentTag() add current selected parent tag : `,
      selectedParentTagId
    );
    fetchTags(parentId, taxonomy);
  };

  const selectSubTag = (children, parentId, checked) => {
    return selectSubTags([children], parentId, checked);
  };

  const selectSubTags = (childrens, parentId, checked) => {
    const base = JSON.parse(JSON.stringify(selectedSubTags));
    const parentIdFound = base.find((el) => el.parentTagId === parentId);
    const parentIdIndex = base.findIndex((el) => el.parentTagId === parentId);
    if (checked) {
      const childrensNotAlreayInTags = [...childrens].filter((tag) => {
        return !parentIdFound.tags.find((children) => {
          return children.id === tag.id;
        });
      });

      if (multipleSelect) {
        parentIdFound.tags = [
          ...parentIdFound.tags,
          ...childrensNotAlreayInTags,
        ];
      } else {
        parentIdFound.tags = [...childrensNotAlreayInTags];
      }
    } else {
      parentIdFound.tags = parentIdFound.tags.filter((tag) => {
        return !childrens.find((children) => children.id === tag.id);
      });
    }

    base[parentIdIndex] = parentIdFound;
    setSelectedSubTags(base);

    onChange(base);

    return base;
  };

  const setAllSelected = (selectedParentTagId, checked) => {
    return selectSubTags(childrens, selectedParentTagId, checked);
  };

  const isSubTagSelected = (childrenId, parentId) => {
    const parentIdFound = selectedSubTags?.find(
      (el) => el.parentTagId === parentId
    );
    const tagIsSelected = parentIdFound?.tags.find(
      (el) => el.id === childrenId
    );
    if (tagIsSelected !== undefined) {
      return true;
    }
    return false;
  };

  const isFiltered = () => {
    const hasSelectedFilters = selectedSubTags?.filter((object1) => {
      return !initialTagsList.some((object2) => {
        return object1.tags.length === object2.tags.length;
      });
    });

    return hasSelectedFilters !== undefined && hasSelectedFilters?.length > 0
      ? true
      : false;
  };

  const onReset = () => {
    setSelectedSubTags(initialTagsList);
  };

  const [showModal, setShowModal] = useState<boolean>(false);

  const openModal = (boolean) => {
    setShowModal(boolean);
  };

  return {
    selectParentTag: selectParentTag,
    setSelectedParentTag: setSelectedParentTag,
    selectedParentTagId: selectedParentTagId,
    childrenTags: childrens,
    selectSubTag: selectSubTag,
    isSubTagSelected: isSubTagSelected,
    selectedSubTags: selectedSubTags,
    setAllSelected: setAllSelected,
    isFiltered: isFiltered,
    onReset: onReset,
    showModal: showModal,
    openModal: openModal,
  };
};

const useFilterChildrens = () => {
  const { wp } = useWP();

  const { state, dispatch } = useContext(AppContext);

  const logger = useLogger(`useTagChildrens`);

  const [childrens, setChildren] = useState<Array<WP_Filter_Children> | null>(
    []
  );

  const fetchTags = (parentId: number, taxonomy) => {
    logger.info('fetchChildrenTags');

    setChildren([]);
    if (parentId) {
      if (taxonomy === 'places-categories') {
        wp.getPlacesChildrenTags(parentId, state.locale).then(
          (places: Array<WP_Place>) => {
            const placesChildren = places.map((place) => {
              return {
                id: place.id,
                name: place.title.rendered,
              };
            });
            if (placesChildren.length <= 0) {
              setChildren(null);
            } else {
              const placesSorted = placesChildren.sort((a, b) =>
                a.name.localeCompare(b.name)
              );
              setChildren(placesSorted);
            }
          }
        );
      } else {
        const cacheKey = `parent:${parentId},tax:${taxonomy}`;
        const children = state.cachedTags.get(cacheKey);

        if (children === undefined) {
          wp.getTaxChildrenTags(parentId, taxonomy).then(
            (tags: Array<WP_Tag>) => {
              if (tags.length <= 0) {
                setChildren(null);
              } else {
                // cache tags
                dispatch({
                  type: ActionType.SetCachedTags,
                  payload: { key: cacheKey, tags: tags },
                });
                setChildren(tags);
              }
            }
          );
        } else {
          setChildren(children);
        }
      }
    } else {
      setChildren([]);
    }
  };

  return { childrens, fetchTags };
};
